import { memo, useCallback } from 'react';
import { SECTION_COLLAPSED_H, type SceneStore } from '@os/doc';
import type { MediaResolver } from '../editor/lod';
import { useChildIds, useObject } from '../editor/useObject';
import { ArrowObject } from './ArrowObject';
import { ConnectorObject } from './ConnectorObject';
import { EmbedObject } from './EmbedObject';
import { LinkObject } from './LinkObject';
import { TableObject } from './TableObject';
import { TaskListObject } from './TaskListObject';
import { PageLinkObject } from './PageLinkObject';
import { frameTransform } from './frame';
import { ImageObject } from './ImageObject';
import { SectionObject } from './SectionObject';
import { ShapeObject } from './ShapeObject';
import { StickyObject } from './StickyObject';
import { TextObject } from './TextObject';

type Props = {
  store: SceneStore;
  id: string;
  media: MediaResolver;
  register: (id: string, el: HTMLElement | null) => void;
  /** Test probe: called on every render of this object. */
  onRender?: (id: string) => void;
};

/** Positioned in world coordinates; React re-renders it only when its own data changes (drags write the DOM directly). */
export const ObjectView = memo(function ObjectView({ store, id, media, register, onRender }: Props) {
  const o = useObject(store, id);
  const childIds = useChildIds(store, id);
  const ref = useCallback((el: HTMLDivElement | null) => register(id, el), [register, id]);
  onRender?.(id);
  if (!o) return null;
  const collapsed = o.type === 'section' && o.collapsed;
  // Children are positioned relative to their container's element: moving it moves them, without a write.
  const kids = collapsed
    ? null
    : childIds.map((c) => <ObjectView key={c} store={store} id={c} media={media} register={register} onRender={onRender} />);
  let content;
  switch (o.type) {
    case 'image':
      content = <ImageObject o={o} media={media} />;
      break;
    case 'text':
      content = <TextObject o={o} />;
      break;
    case 'sticky':
      content = <StickyObject o={o} />;
      break;
    case 'shape':
      content = <ShapeObject o={o} />;
      break;
    case 'arrow':
      content = <ArrowObject o={o} />;
      break;
    case 'section':
      content = <SectionObject o={o} store={store} hidden={collapsed ? childIds.length : 0} />;
      break;
    case 'connector':
      content = <ConnectorObject o={o} store={store} />;
      break;
    case 'link':
      content = <LinkObject o={o} media={media} />;
      break;
    case 'embed':
      content = <EmbedObject o={o} media={media} />;
      break;
    case 'table':
      content = <TableObject o={o} store={store} />;
      break;
    case 'tasklist':
      content = <TaskListObject o={o} store={store} />;
      break;
    case 'pagelink':
      content = <PageLinkObject o={o} store={store} media={media} />;
      break;
  }
  // Arrow heads, connectors and section titles overflow their box, and containers show their children: no paint containment.
  const paintsOutside = o.type === 'arrow' || o.type === 'section' || o.type === 'group' || o.type === 'connector' || o.type === 'table';
  return (
    <div
      ref={ref}
      data-id={id}
      data-type={o.type}
      {...(o.type === 'image' ? { 'data-photo-frame': '' } : {})}
      className={`absolute left-0 top-0 ${o.type === 'image' ? 'rounded-[3px]' : ''}`}
      style={{
        // An image is a photo print (P5e): a thin white border and a light shadow drawn outside its box (ratio untouched).
        boxShadow: o.type === 'image' ? '0 0 0 6px #ffffff, 0 2px 10px 6px rgb(0 0 0 / 0.12)' : undefined,
        transform: o.type === 'connector' ? frameTransform({ x: 0, y: 0, w: 1, h: 1, rotation: 0 }) : frameTransform(o),
        width: o.type === 'connector' ? 1 : o.w,
        height: o.type === 'connector' ? 1 : collapsed ? SECTION_COLLAPSED_H : o.h,
        contain: paintsOutside ? 'layout style' : 'layout paint style',
      }}
    >
      {content}
      {/* A frame clips its children, never its title row above it (P5e). */}
      {o.type === 'section' && o.clip ? (
        <div data-clip className="absolute inset-0 overflow-hidden">
          {kids}
        </div>
      ) : (
        kids
      )}
    </div>
  );
});
